import { useForm } from 'react-hook-form';
import { useTheme } from '../../hooks/useTheme';
import { useEffect } from 'react';
import { stepOneContent } from '../../data/stepone';
import { PhoneInput } from '../PhoneInput';

interface StepOneProps {
  onValidChange: (isValid: boolean) => void;
  formData: {
    name: string;
    email: string;
    phone: string;
    company?: string;
  };
  onFormChange: (data: any) => void;
}

export function StepOne({ onValidChange, formData, onFormChange }: StepOneProps) {
  const theme = useTheme(state => state.getThemeStyles());
  const { register, handleSubmit, formState: { errors, isValid }, watch, setValue } = useForm({
    mode: 'onChange',
    defaultValues: formData
  });

  // Remover o watch geral e observar campos específicos
  const name = watch('name');
  const email = watch('email');
  const phone = watch('phone');

  useEffect(() => {
    // Atualizar form data apenas quando os valores realmente mudarem
    const newFormData = {
      name: name || '',
      email: email || '',
      phone: phone || ''
    };
    
    onFormChange(newFormData);
  }, [name, email, phone, onFormChange]);

  // Efeito separado para validação
  useEffect(() => {
    onValidChange(isValid);
  }, [isValid, onValidChange]);

  return (
    <div className="space-y-6">
      <div className={theme.components.input.variants.form.group}>
        <label 
          htmlFor="name" 
          className={`${theme.components.input.variants.form.label.base} ${theme.components.input.variants.form.label.required}`}
        >
          {stepOneContent.inputs.name.label}
        </label>
        <input
          {...register('name', { required: true })}
          type="text"
          id="name"
          className={`${theme.components.input.variants.form.input} ${theme.components.input.states.focus}`}
          placeholder={stepOneContent.inputs.name.placeholder}
        />
        {errors.name && (
          <span className={theme.components.input.variants.form.error.message}>
            {stepOneContent.inputs.name.error}
          </span>
        )}
      </div>
      <div className={theme.components.input.variants.form.group}>
        <label 
          htmlFor="email" 
          className={`${theme.components.input.variants.form.label.base} ${theme.components.input.variants.form.label.required}`}
        >
          {stepOneContent.inputs.email.label}
        </label>
        <input
          {...register('email', { 
            required: true,
            pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
          })}
          type="email"
          id="email"
          className={`${theme.components.input.variants.form.input} ${theme.components.input.states.focus}`}
          placeholder={stepOneContent.inputs.email.placeholder}
        />
        {errors.email?.type === 'required' && (
          <span className={theme.components.input.variants.form.error.message}>
            {stepOneContent.inputs.email.errors.required}
          </span>
        )}
        {errors.email?.type === 'pattern' && (
          <span className={theme.components.input.variants.form.error.message}>
            {stepOneContent.inputs.email.errors.invalid}
          </span>
        )}
      </div>
      <div className={theme.components.input.variants.form.group}>
        <label 
          htmlFor="phone" 
          className={`${theme.components.input.variants.form.label.base} ${theme.components.input.variants.form.label.required}`}
        >
          {stepOneContent.inputs.phone.label}
        </label>
        <PhoneInput
          value={watch('phone') || ''}
          onChange={(value) => {
            setValue('phone', value, { shouldValidate: true });
          }}
          className={`${theme.components.input.variants.form.input} ${theme.components.input.states.focus}`}
        />
        {errors.phone && (
          <span className={theme.components.input.variants.form.error.message}>
            {stepOneContent.inputs.phone.errors.required}
          </span>
        )}
      </div>
      <div className={theme.components.input.variants.form.group}>
        <label 
          htmlFor="company" 
          className={theme.components.input.variants.form.label.base}
        >
          {stepOneContent.inputs.company.label}
          <span className={theme.components.input.variants.form.label.optional}>
            {stepOneContent.inputs.company.optional}
          </span>
        </label>
        <input
          {...register('company')}
          type="text"
          id="company"
          className={`${theme.components.input.variants.form.input} ${theme.components.input.states.focus}`}
          placeholder={stepOneContent.inputs.company.placeholder}
        />
      </div>
    </div>
  );
}